<script setup lang="ts">
import { computed, ref, watch } from "vue";

const props = defineProps<{
  min: number;
  max: number;
  step?: number;
  modelValue: [number, number]; // [min, max]
  unit?: string;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", val: [number, number]): void;
}>();

// Local state for smooth dragging
const lower = ref(props.modelValue[0]);
const upper = ref(props.modelValue[1]);

watch(
  () => props.modelValue,
  (val) => {
    lower.value = val[0];
    upper.value = val[1];
  }
);

function onInputLower(e: Event) {
  const val = Number((e.target as HTMLInputElement).value);
  // Prevent crossing
  if (val > upper.value) {
    lower.value = upper.value;
  } else {
    lower.value = val;
  }
  emit("update:modelValue", [lower.value, upper.value]);
}

function onInputUpper(e: Event) {
  const val = Number((e.target as HTMLInputElement).value);
  // Prevent crossing
  if (val < lower.value) {
    upper.value = lower.value;
  } else {
    upper.value = val;
  }
  emit("update:modelValue", [lower.value, upper.value]);
}

// Calculate percentages for the track background
const minPct = computed(() => {
  const range = props.max - props.min;
  return ((lower.value - props.min) / range) * 100;
});

const maxPct = computed(() => {
  const range = props.max - props.min;
  return ((upper.value - props.min) / range) * 100;
});

// Detect if labels would overlap (if handles are too close)
const labelsOverlap = computed(() => {
  return Math.abs(maxPct.value - minPct.value) < 20; // Less than 20% apart
});

// Smart positioning: stack labels vertically when they overlap
const lowerLabelStyle = computed(() => {
  if (labelsOverlap.value) {
    return {
      left: minPct.value + '%',
      top: '12px', // Position below slider
    };
  }
  return {
    left: minPct.value + '%',
    top: '12px',
  };
});

const upperLabelStyle = computed(() => {
  if (labelsOverlap.value) {
    return {
      left: maxPct.value + '%',
      top: '-32px', // Position above slider
    };
  }
  return {
    left: maxPct.value + '%',
    top: '12px', // Same level when not overlapping
  };
});

const trackStyle = computed(() => {
  return {
    background: `linear-gradient(to right, 
      #1b222b ${minPct.value}%, 
      #00b3b3 ${minPct.value}%, 
      #00b3b3 ${maxPct.value}%, 
      #1b222b ${maxPct.value}%)`
  };
});
</script>

<template>
  <div class="dual-range-wrapper">
    <div class="values">
      <!-- Often specific UI wants value above or below handle, 
           here we put them centered or follows handle. 
           For this specific design, screenshot shows values below handles. -->
      <span class="val-label lower" :class="{ stacked: labelsOverlap }" :style="lowerLabelStyle">{{ lower }}{{ unit }}</span>
      <span class="val-label upper" :class="{ stacked: labelsOverlap }" :style="upperLabelStyle">{{ upper }}{{ unit }}</span>
    </div>

    <div class="slider-container">
      <div class="track-bg" :style="trackStyle"></div>
      
      <input
        type="range"
        :min="min"
        :max="max"
        :step="step || 1"
        :value="lower"
        @input="onInputLower"
        class="thumb thumb-zindex-3"
        :class="{ 'thumb-zindex-4': lower > max - (max-min)/2 }"
      />
      
      <input
        type="range"
        :min="min"
        :max="max"
        :step="step || 1"
        :value="upper"
        @input="onInputUpper"
        class="thumb thumb-zindex-4"
      />
    </div>
  </div>
</template>

<style scoped>
.dual-range-wrapper {
  position: relative;
  width: 100%;
  padding: 20px 0 10px 0; /* space for labels */
  height: 60px;
}

.values {
  position: relative;
  width: 100%;
  height: 20px;
  margin-top: 14px; /* push below slider */
}

.val-label {
  position: absolute;
  transform: translateX(-50%);
  font-size: 0.85rem;
  font-weight: 600;
  color: #e6e6e6;
  background: rgba(13, 13, 13, 0.95);
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  border: 1px solid #2a2f34;
  border-color: #00b3b3;
}

.val-label.stacked {
  background: rgba(13, 13, 13, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
  font-weight: 700;
}

.slider-container {
  position: relative;
  width: 100%;
  height: 6px;
  top: -24px; /* Move back up to align with previous space */
}

/* Custom Track Background */
.track-bg {
  position: absolute;
  width: 100%;
  height: 6px;
  border-radius: 999px;
  top: 0;
  z-index: 1;
}

/* Range Inputs */
input[type="range"] {
  position: absolute;
  pointer-events: none; /* Let clicks pass through to track/other thumb */
  appearance: none;
  -webkit-appearance: none;
  z-index: 2;
  height: 6px;
  width: 100%;
  opacity: 0;
  margin: 0;
}

input[type="range"]::-webkit-slider-thumb {
  pointer-events: all;
  width: 28px;
  height: 28px;
  appearance: none;
  -webkit-appearance: none;
  background: linear-gradient(135deg, #8ab4f8 0%, #5a9df8 100%);
  border: 3px solid #ffffff;
  border-radius: 50%;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.4), 0 1px 3px rgba(0, 0, 0, 0.3);
  cursor: grab;
  margin-top: 0px;
  transition: all 0.15s ease;
}

input[type="range"]::-webkit-slider-thumb:hover {
  background: linear-gradient(135deg, #a8c4f9 0%, #7aabf9 100%);
  border-color: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.45), 0 2px 6px rgba(0, 0, 0, 0.35);
  transform: scale(1.15);
}

input[type="range"]::-webkit-slider-thumb:active {
  cursor: grabbing;
  background: linear-gradient(135deg, #6a9df8 0%, #4a8df8 100%);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.5), inset 0 1px 3px rgba(0, 0, 0, 0.2);
  transform: scale(1.1);
}

/* Firefox */
input[type="range"]::-moz-range-thumb {
  pointer-events: all;
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #8ab4f8 0%, #5a9df8 100%);
  border: 3px solid #ffffff;
  border-radius: 50%;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.4), 0 1px 3px rgba(0, 0, 0, 0.3);
  cursor: grab;
  transition: all 0.15s ease;
}

input[type="range"]::-moz-range-thumb:hover {
  background: linear-gradient(135deg, #a8c4f9 0%, #7aabf9 100%);
  border-color: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.45), 0 2px 6px rgba(0, 0, 0, 0.35);
  transform: scale(1.15);
}

input[type="range"]::-moz-range-thumb:active {
  cursor: grabbing;
  background: linear-gradient(135deg, #6a9df8 0%, #4a8df8 100%);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.5), inset 0 1px 3px rgba(0, 0, 0, 0.2);
  transform: scale(1.1);
}

/* Z-Index tricks to make sure both handles are reachable if they overlap */
.thumb-zindex-3 {
  z-index: 3;
}
.thumb-zindex-4 {
  z-index: 4;
}
</style>
